<template>
  <div class="inner-content">
    <section>
      <div class="container-fluid">
        <div class="row m-0">
          <div class="col-12">
            <div class="card">
              <div class="card-header card-header-primary d-flex flex-wrap align-items-center position-relative py-4">
                <div class="content-card-left">
                  <h4 class="card-title text-white">Sales &amp; Stock Report</h4>
                  <p class="card-category mb-0 text-white">
                    Opening, purchases, sales and closing stock by manufacturer
                  </p>
                </div>
              </div>

              <div class="card-body">
                <!-- Filters -->
                <form class="table-header mx-0 mb-4 custom-form" @submit.prevent="loadReport">
                  <div class="row m-0 align-items-center">
                    <div class="col-lg-4 col-md-5 col-12 px-0 pe-lg-3 mb-lg-0 mb-3">
                      <Multiselect
                        v-model="companyId"
                        :options="companies"
                        class="custom-multiselect"
                        placeholder="Select Manufacturer"
                        :searchable="true"
                        mode="single"
                        label="name"
                        trackBy="name"
                        valueProp="id"
                      />
                    </div>
                    <div class="col-lg-5 col-md-5 col-12 px-0 mb-lg-0 mb-3">
                      <v-date-picker
                        class="custom-calendar left-caret-none"
                        popover-align="right"
                        v-model="range"
                        is-range
                        :columns="$screens({ default: 2, lg: 2 })"
                        :masks="{ input: 'DD/MM/YY' }"
                      >
                        <template v-slot="{ inputValue, inputEvents }">
                          <div class="d-flex flex-wrap align-items-center">
                            <div class="position-relative calendar-input-wrapper clearfix">
                              <span class="d-inline-block me-3 float-left">From</span>
                              <i class="bi bi-calendar3 position-absolute icon-left-placement"></i>
                              <input :value="inputValue.start" v-on="inputEvents.start"
                                class="border-0 icon-left position-relative float-left" />
                            </div>
                            <div class="position-relative calendar-input-wrapper clearfix ms-3">
                              <span class="d-inline-block me-3 float-left">To</span>
                              <i class="bi bi-calendar3 position-absolute icon-left-placement"></i>
                              <input :value="inputValue.end" v-on="inputEvents.end"
                                class="border-0 icon-left position-relative float-left" />
                            </div>
                          </div>
                        </template>
                      </v-date-picker>
                    </div>
                    <div class="col-lg-3 col-md-2 col-12 px-0 ps-lg-3">
                      <button type="submit" class="btn btn-primary w-100" :disabled="loading">
                        <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                        Generate
                      </button>
                    </div>
                  </div>
                </form>

                <div v-if="companyName" class="mb-2 fw-bold">
                  {{ companyName }} &nbsp;|&nbsp; {{ rangeLabel }}
                </div>

                <div class="table-responsive mb-0">
                  <table class="stock-report-table">
                    <thead>
                      <tr>
                        <th rowspan="2">#</th>
                        <th rowspan="2">Description</th>
                        <th rowspan="2">Packing</th>
                        <th rowspan="2" class="text-end">Price</th>
                        <th colspan="2" class="text-center">Opening Stock</th>
                        <th colspan="3" class="text-center">Purchases</th>
                        <th colspan="2" class="text-center">Total Stock</th>
                        <th colspan="2" class="text-center">Sales</th>
                        <th colspan="2" class="text-center">Returns</th>
                        <th colspan="2" class="text-center">Closing Stock</th>
                        <th rowspan="2" class="text-center">Stock Out</th>
                        <th rowspan="2" class="text-end">Net Value</th>
                      </tr>
                      <tr>
                        <th class="text-center">CTN</th><th class="text-center">PCS</th>
                        <th class="text-center">CTN</th><th class="text-center">PCS</th><th class="text-center" title="Free extra boxes received with purchases (included in Purchases)">EXTRA</th>
                        <th class="text-center">CTN</th><th class="text-center">PCS</th>
                        <th class="text-center">CTN</th><th class="text-center">PCS</th>
                        <th class="text-center">CTN</th><th class="text-center">PCS</th>
                        <th class="text-center">CTN</th><th class="text-center">PCS</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, i) in rows" :key="row.id">
                        <td>{{ i + 1 }}</td>
                        <td>{{ row.name }}</td>
                        <td class="text-center">{{ row.packing }}</td>
                        <td class="text-end">{{ fmt(row.price) }}</td>
                        <td class="text-center">{{ row.opening.ctn || '-' }}</td>
                        <td class="text-center">{{ row.opening.pcs || '-' }}</td>
                        <td class="text-center">{{ row.purchases.ctn || '-' }}</td>
                        <td class="text-center">{{ row.purchases.pcs || '-' }}</td>
                        <td class="text-center extra-col">{{ row.extra || '-' }}</td>
                        <td class="text-center">{{ row.total_stock.ctn || '-' }}</td>
                        <td class="text-center">{{ row.total_stock.pcs || '-' }}</td>
                        <td class="text-center">{{ row.sales.ctn || '-' }}</td>
                        <td class="text-center">{{ row.sales.pcs || '-' }}</td>
                        <td class="text-center">{{ row.returns.ctn || '-' }}</td>
                        <td class="text-center">{{ row.returns.pcs || '-' }}</td>
                        <td class="text-center">{{ row.closing.ctn || '-' }}</td>
                        <td class="text-center">{{ row.closing.pcs || '-' }}</td>
                        <td class="text-center">
                          <span v-if="row.stock_out" class="badge bg-danger">OUT</span>
                          <span v-else>-</span>
                        </td>
                        <td class="text-end">{{ fmt(row.net_value) }}</td>
                      </tr>
                      <tr v-if="rows.length === 0">
                        <td colspan="20" class="text-center">
                          {{ loaded ? 'No products found.' : 'Select a manufacturer and date range, then Generate.' }}
                        </td>
                      </tr>
                      <tr v-if="rows.length" class="fw-bold grand-total-row">
                        <td colspan="4" class="text-end">GRAND TOTAL</td>
                        <td class="text-center">{{ grandTotal.opening.ctn || '-' }}</td>
                        <td class="text-center">{{ grandTotal.opening.pcs || '-' }}</td>
                        <td class="text-center">{{ grandTotal.purchases.ctn || '-' }}</td>
                        <td class="text-center">{{ grandTotal.purchases.pcs || '-' }}</td>
                        <td class="text-center extra-col">{{ grandTotal.extra || '-' }}</td>
                        <td class="text-center">{{ grandTotal.total_stock.ctn || '-' }}</td>
                        <td class="text-center">{{ grandTotal.total_stock.pcs || '-' }}</td>
                        <td class="text-center">{{ grandTotal.sales.ctn || '-' }}</td>
                        <td class="text-center">{{ grandTotal.sales.pcs || '-' }}</td>
                        <td class="text-center">{{ grandTotal.returns.ctn || '-' }}</td>
                        <td class="text-center">{{ grandTotal.returns.pcs || '-' }}</td>
                        <td class="text-center">{{ grandTotal.closing.ctn || '-' }}</td>
                        <td class="text-center">{{ grandTotal.closing.pcs || '-' }}</td>
                        <td class="text-center">{{ grandTotal.stock_out || '-' }}</td>
                        <td class="text-end">{{ fmt(totalNetValue) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div v-if="rows.length" class="text-end small text-muted mt-2">
                  Note: CTN = Carton | PCS = Pieces
                </div>

                <!-- Value summary -->
                <div v-if="rows.length" class="stock-summary mt-4">
                  <div class="row m-0 text-center">
                    <div v-for="card in valueCards" :key="card.key" class="col-lg col-md-4 col-6 mb-3">
                      <div class="summary-label">{{ card.label }}</div>
                      <div class="summary-value">{{ fmt(totals[card.key]) }}</div>
                    </div>
                  </div>
                  <hr class="my-2" />
                  <h6 class="sale-type-title mt-3 mb-2">Sales by Sale Type</h6>
                  <div class="table-responsive">
                    <table class="sale-type-table mb-3">
                      <thead>
                        <tr>
                          <th class="label-col">Description</th>
                          <th v-for="t in saleTypes" :key="t.key">{{ t.label }}</th>
                          <th class="total-col">Total</th>
                        </tr>
                      </thead>
                      <tbody v-for="group in saleTypeGroups" :key="group.title">
                        <tr class="group-row">
                          <td :colspan="saleTypes.length + 2">{{ group.title }}</td>
                        </tr>
                        <tr v-for="line in group.lines" :key="line.key" :class="`line-${line.key}`">
                          <td class="label-col">{{ line.label }}</td>
                          <td v-for="t in saleTypes" :key="t.key" :class="marginClass(line, line.value(t.key))">
                            {{ fmt(line.value(t.key)) }}
                          </td>
                          <td class="total-col" :class="marginClass(line, saleTypeTotal(line))">
                            {{ fmt(saleTypeTotal(line)) }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import Multiselect from '@vueform/multiselect';
import '@vueform/multiselect/themes/default.css';
import moment from 'moment';
import CompanyServices from '@/services/CompanyServices';
import ReportServices from '@/services/ReportServices';

export default {
  name: 'StockReport',
  components: { Multiselect },
  setup() {
    const companies = ref([]);
    const companyId = ref(null);
    const companyName = ref('');
    const range = ref({
      start: moment().startOf('month').toDate(),
      end: moment().toDate(),
    });
    const rows = ref([]);
    const totalNetValue = ref(0);
    const totals = ref({});

    const valueCards = [
      { key: 'opening_value', label: 'Opening Stock Value' },
      { key: 'purchase_value', label: 'Purchase Value' },
      { key: 'total_stock_value', label: 'Total Stock Value' },
      { key: 'sales_value', label: 'Sales Value' },
      { key: 'return_value', label: 'Return Value' },
      { key: 'net_sales_value', label: 'Net Sales Value' },
      { key: 'stock_out_value', label: 'Stock Out Value' },
      { key: 'closing_value', label: 'Closing Stock Value' },
    ];
    // Sales and returns split by the sale type of each bill: at initial price
    // (matches the stock values above) and at the billed (actual) price.
    const saleTypes = [
      { key: 'initial', label: 'Initial' },
      { key: 'middle', label: 'Middle' },
      { key: 'wholesale', label: 'Wholesale' },
      { key: 'retail', label: 'Retail' },
    ];
    const typeValue = (prefix, type) => Number(totals.value[`${prefix}_${type}_value`] || 0);
    const netAtInitial = (t) => typeValue('sales', t) - typeValue('returns', t);
    const netBilled = (t) => typeValue('billed_sales', t) - typeValue('billed_returns', t);
    const saleTypeGroups = [
      {
        title: 'At Initial Price (stock value)',
        lines: [
          { key: 'sales', label: 'Sales', value: (t) => typeValue('sales', t) },
          { key: 'returns', label: '(−) Returns', value: (t) => typeValue('returns', t) },
          { key: 'net', label: 'Net Sales', value: netAtInitial },
        ],
      },
      {
        title: 'At Actual Price (billed)',
        lines: [
          { key: 'billed', label: 'Net Billed Amount', value: netBilled },
          { key: 'margin', label: 'Margin (Billed − Net Sales)', value: (t) => netBilled(t) - netAtInitial(t) },
        ],
      },
    ];
    const saleTypeTotal = (line) => saleTypes.reduce((sum, t) => sum + line.value(t.key), 0);
    // Margin cells: green when positive, red when negative.
    const marginClass = (line, value) => {
      if (line.key !== 'margin' || Math.abs(value) < 0.005) return '';
      return value > 0 ? 'margin-pos' : 'margin-neg';
    };

    // Column sums for the GRAND TOTAL row (CTN and PCS summed separately).
    const grandTotal = computed(() => {
      const sum = (key) => rows.value.reduce(
        (acc, r) => ({ ctn: acc.ctn + Number(r[key].ctn || 0), pcs: acc.pcs + Number(r[key].pcs || 0) }),
        { ctn: 0, pcs: 0 }
      );
      return {
        opening: sum('opening'),
        purchases: sum('purchases'),
        returns: sum('returns'),
        total_stock: sum('total_stock'),
        sales: sum('sales'),
        closing: sum('closing'),
        extra: rows.value.reduce((acc, r) => acc + Number(r.extra || 0), 0),
        stock_out: rows.value.filter((r) => r.stock_out).length,
      };
    });
    const loading = ref(false);
    const loaded = ref(false);

    const fmt = (v) =>
      Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

    const rangeLabel = computed(() => {
      const s = range.value.start ? moment(range.value.start).format('DD-MMM-YYYY') : '';
      const e = range.value.end ? moment(range.value.end).format('DD-MMM-YYYY') : '';
      return `${s} to ${e}`;
    });

    const loadCompanies = async () => {
      const res = await CompanyServices.paginateCompanies();
      companies.value = res.data.data || [];
    };

    const loadReport = async () => {
      if (!companyId.value) {
        alert('Please select a manufacturer');
        return;
      }
      loading.value = true;
      try {
        const from = moment(range.value.start).format('YYYY-MM-DD');
        const to = moment(range.value.end).format('YYYY-MM-DD');
        const res = await ReportServices.getSalesStockReport(companyId.value, from, to);
        rows.value = res.data.rows || [];
        totalNetValue.value = res.data.total_net_value || 0;
        totals.value = res.data.totals || {};
        companyName.value = res.data.company ? res.data.company.name : '';
        loaded.value = true;
      } catch (e) {
        rows.value = [];
        totalNetValue.value = 0;
        totals.value = {};
      } finally {
        loading.value = false;
      }
    };

    loadCompanies();

    return {
      companies, companyId, companyName, range, rows, totalNetValue,
      totals, valueCards, saleTypes, saleTypeGroups, saleTypeTotal, marginClass, grandTotal,
      loading, loaded, fmt, rangeLabel, loadReport,
    };
  },
};
</script>

<style scoped>
.stock-report-table {
  width: 100%;
  min-width: 1100px;
  border-collapse: collapse;
  border: 1px solid #adb5bd;
}
.stock-report-table th,
.stock-report-table td {
  font-size: 12px;
  padding: 6px 8px;
  white-space: nowrap;
  border: 1px solid #ced4da;
}
.stock-report-table thead th {
  background: #f1f3f5;
  font-weight: 700;
  color: #212529;
  vertical-align: middle;
}
.stock-report-table thead tr:last-child th {
  border-bottom: 2px solid #495057;
}
.stock-report-table tbody tr:nth-child(even) td {
  background: #fafafa;
}
.stock-report-table tbody tr:hover td {
  background: #f1f7ff;
}
.stock-report-table .extra-col {
  color: #6c757d;
}
.stock-report-table .grand-total-row td {
  border-top: 2px solid #333;
  background: #e9ecef;
}
.stock-summary .summary-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #555;
}
.stock-summary .summary-value {
  font-size: 18px;
  font-weight: 700;
}
.sale-type-title {
  font-weight: 700;
  text-transform: uppercase;
  font-size: 13px;
  color: #555;
}
.sale-type-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #dee2e6;
}
.sale-type-table th,
.sale-type-table td {
  padding: 8px 12px;
  font-size: 13px;
  text-align: right;
  white-space: nowrap;
  border-bottom: 1px solid #dee2e6;
}
.sale-type-table thead th {
  background: #f1f3f5;
  font-weight: 700;
  border-bottom: 2px solid #333;
}
.sale-type-table .label-col {
  text-align: left;
  min-width: 190px;
}
.sale-type-table .total-col {
  font-weight: 700;
  background: #f8f9fa;
  border-left: 2px solid #333;
}
.sale-type-table thead .total-col {
  background: #e9ecef;
}
.sale-type-table .group-row td {
  text-align: left;
  font-weight: 700;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #fff;
  background: #6c757d;
}
.sale-type-table .line-returns td {
  color: #6c757d;
}
.sale-type-table .line-net td,
.sale-type-table .line-margin td {
  font-weight: 700;
  border-top: 2px solid #333;
  background: #fff8e1;
}
.sale-type-table .line-net .total-col,
.sale-type-table .line-margin .total-col {
  background: #ffecb3;
}
.sale-type-table .margin-pos {
  color: #198754;
}
.sale-type-table .margin-neg {
  color: #dc3545;
}
</style>
